// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.
import { Button, Card, Typography } from '@mui/material';
import { AppIcon } from '@shared/ui-kit/components';
import { Translation } from '@shared/ui-kit/providers';
import { defineComponent } from '@shared/ui-kit/utils/define-component';
import { ScreenLayout } from '@shared/advisory/account-workflow/layout';
import { useSubmittedRequest } from './use-submitted-request';

export const SubmittedRequest = defineComponent({
  displayName: 'AccountOpeningSubmittedRequest',
  useSetup: useSubmittedRequest,
  View: ({ onFinish }) => (
    <ScreenLayout
      actions={
        <div className="flex shrink-0 items-center justify-center border-t p-8">
          <Button
            type="button"
            variant="contained"
            color="secondary"
            size="large"
            onClick={onFinish}
            startIcon={<AppIcon icon="Home" size="lg" color="light" />}
            data-testid="submitted-request-back-to-home"
          >
            <Translation
              i18nKey="onboarding-process-overview.confirmation.back-to-home-button"
              ns="components"
            />
          </Button>
        </div>
      }
    >
      <div className="flex flex-col items-center justify-center gap-7 pb-10 pt-4">
        <div className="flex h-24 w-24 items-center justify-center rounded-full bg-page">
          <AppIcon icon="CheckCircle" size={32} color="success" />
        </div>

        <div className="flex flex-col items-center gap-3 text-center">
          <Typography
            variant="h3"
            className="!text-h3 !font-semibold !text-title-navy"
            data-testid="submitted-request-title"
          >
            <Translation
              i18nKey="account-opening.submitted-request.title"
              ns="components"
            />
          </Typography>

          <Typography
            variant="body1"
            className="!text-base !font-normal !text-subtitle"
            data-testid="submitted-request-subtitle"
          >
            <Translation
              i18nKey="account-opening.submitted-request.subtitle"
              ns="components"
            />
          </Typography>

          <Typography
            variant="body1"
            className="max-w-lg !text-base !font-normal !leading-7 !text-subtitle"
            data-testid="submitted-request-description"
          >
            <Translation
              i18nKey="account-opening.submitted-request.description"
              ns="components"
            />
          </Typography>
        </div>

        <div className="flex max-w-xl flex-col items-stretch gap-3">
          <Card
            variant="outlined"
            className="!rounded-2xl !border-border-light !bg-white !px-4 !py-4"
            data-testid="submitted-request-status-request-received"
          >
            <div className="flex items-center gap-3">
              <AppIcon
                icon="CheckCircle"
                size="lg"
                color="dark"
                className="shrink-0"
              />

              <Typography
                variant="body1"
                className="!text-base !font-normal !text-title-navy"
              >
                <Translation
                  i18nKey="account-opening.submitted-request.status.request-received"
                  ns="components"
                />
              </Typography>
            </div>
          </Card>

          <Card
            variant="outlined"
            className="!rounded-2xl !border-border-light !bg-white !px-4 !py-4"
            data-testid="submitted-request-status-account-activation"
          >
            <div className="flex items-center gap-3">
              <AppIcon icon="Clock" size="lg" color="dark" className="shrink-0" />

              <Typography
                variant="body1"
                className="!text-base !font-normal !text-title-navy"
              >
                <Translation
                  i18nKey="account-opening.submitted-request.status.account-activation"
                  ns="components"
                />
              </Typography>
            </div>
          </Card>

          <Card
            variant="outlined"
            className="!rounded-2xl !border-border-light !bg-white !px-4 !py-4"
            data-testid="submitted-request-status-contract-signature-email"
          >
            <div className="flex items-center gap-3">
              <AppIcon icon="PenTool" size="lg" color="dark" className="shrink-0" />

              <Typography
                variant="body1"
                className="!text-base !font-normal !text-title-navy"
              >
                <Translation
                  i18nKey="account-opening.submitted-request.status.contract-signature-email"
                  ns="components"
                />
              </Typography>
            </div>
          </Card>

          <Card
            variant="outlined"
            className="!rounded-2xl !border-border-light !bg-section !p-5"
            data-testid="submitted-request-email-card"
          >
            <div className="flex items-start gap-3">
              <AppIcon
                icon="Mail"
                size="lg"
                color="dark"
                className="mt-0.5 shrink-0"
              />

              <div className="flex flex-col gap-2">
                <Typography
                  variant="h5"
                  className="!text-base !font-semibold !text-title-navy"
                >
                  <Translation
                    i18nKey="account-opening.submitted-request.email-card.title"
                    ns="components"
                  />
                </Typography>

                <Typography
                  variant="body2"
                  className="!text-sm !font-normal !leading-7 !text-subtitle"
                >
                  <Translation
                    i18nKey="account-opening.submitted-request.email-card.description"
                    ns="components"
                  />
                </Typography>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </ScreenLayout>
  ),
});
